import { useState } from 'react';
import { useKPI } from '../hooks/useKPI';
import { ROLE_META } from '../utils/constants';
import { StatusBadge, RoleBadge, OnlineIndicator, Button } from '../components/common';

/**
 * KPI & Performance Page - Phase 1: Core Tracking
 * Admin dashboard for monitoring team performance
 */
export function KPIMetricsPage() {
  const [roleFilter, setRoleFilter] = useState('all');
  const { teamMembers, summary, loading, error, reload } = useKPI(roleFilter);

  return (
    <div style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h1 style={{ color: "#f1f5f9", fontSize: 28, fontWeight: 900 }}>
            📈 مؤشرات الأداء (KPI)
          </h1>
          <Button 
            onClick={reload} 
            variant="secondary"
            disabled={loading}
          >
            {loading ? 'جاري التحميل...' : '🔄 تحديث'}
          </Button>
        </div>
        <p style={{ color: "#94a3b8", fontSize: 14 }}>
          تتبع أداء الفريق وتحقيق الأهداف
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          background: "#7f1d1d",
          border: "1px solid #f87171",
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          color: "#fecaca"
        }}>
          ❌ خطأ: {error}
        </div>
      )}

      {/* Role Filter */}
      <div style={{
        display: "flex",
        gap: 12,
        marginBottom: 24,
        flexWrap: "wrap"
      }}>
        {[
          { key: 'all', label: 'جميع الموظفين', icon: '👥' },
          { key: 'marketer', label: 'منسقات الصفقات', icon: '📣' },
          { key: 'rep', label: 'المندوبين الميدانيين', icon: '🤝' },
        ].map(filter => (
          <button
            key={filter.key}
            onClick={() => setRoleFilter(filter.key)}
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              border: "none",
              background: roleFilter === filter.key 
                ? "linear-gradient(135deg,#0ea5e9,#2563eb)" 
                : "#0c1525",
              color: roleFilter === filter.key ? "#fff" : "#94a3b8",
              cursor: "pointer",
              fontFamily: "'Cairo',sans-serif",
              fontSize: 13,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 8,
              transition: "all 0.2s",
            }}
          >
            {filter.icon} {filter.label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ 
          padding: 48, 
          textAlign: "center", 
          color: "#94a3b8" 
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <p style={{ fontSize: 16, fontWeight: 700 }}>جاري تحميل بيانات الأداء...</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Summary Cards */}
          {summary && (
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", 
              gap: 16, 
              marginBottom: 32 
            }}>
              <div style={{
                background: "linear-gradient(135deg, #162050, #1e2a6e)",
                border: "1px solid #3b82f6",
                borderRadius: 16,
                padding: 24,
              }}>
                <p style={{ color: "#93c5fd", fontSize: 12, marginBottom: 8 }}>إجمالي الموظفين</p>
                <p style={{ color: "#3b82f6", fontSize: 32, fontWeight: 900 }}>
                  {summary.totalMembers}
                </p>
              </div>

              <div style={{
                background: "linear-gradient(135deg, #052e16, #0a3d1f)",
                border: "1px solid #10b981",
                borderRadius: 16,
                padding: 24,
              }}>
                <p style={{ color: "#6ee7b7", fontSize: 12, marginBottom: 8 }}>المطاعم المضافة</p>
                <p style={{ color: "#34d399", fontSize: 32, fontWeight: 900 }}>
                  {summary.totalRestaurants}
                </p>
              </div>

              <div style={{
                background: "linear-gradient(135deg, #3d2000, #4d2a00)",
                border: "1px solid #f59e0b",
                borderRadius: 16,
                padding: 24,
              }}>
                <p style={{ color: "#fcd34d", fontSize: 12, marginBottom: 8 }}>الصفقات المغلقة</p>
                <p style={{ color: "#f59e0b", fontSize: 32, fontWeight: 900 }}>
                  {summary.totalDeals}
                </p>
              </div>

              <div style={{
                background: "linear-gradient(135deg, #200d50, #2a1170)",
                border: "1px solid #a78bfa",
                borderRadius: 16,
                padding: 24,
              }}>
                <p style={{ color: "#c4b5fd", fontSize: 12, marginBottom: 8 }}>متوسط معدل التحويل</p>
                <p style={{ color: "#a78bfa", fontSize: 32, fontWeight: 900 }}>
                  {summary.avgConversionRate}%
                </p>
              </div>
            </div>
          )}

          {/* Team Performance Table */}
          <div style={{
            background: "#0c1525",
            borderRadius: 16,
            border: "1px solid #162035",
            overflow: "hidden"
          }}>
            <div style={{ 
              padding: 20, 
              borderBottom: "1px solid #162035",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <h2 style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 700, margin: 0 }}>
                🏆 أداء الفريق
              </h2>
              {summary?.topPerformer && (
                <div style={{
                  background: "#052e16",
                  border: "1px solid #10b981",
                  borderRadius: 10,
                  padding: "8px 16px",
                  color: "#34d399",
                  fontSize: 12,
                  fontWeight: 700
                }}>
                  🌟 الأفضل: {summary.topPerformer.name}
                </div>
              )}
            </div>

            {teamMembers.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: "#64748b" }}>
                <p style={{ fontSize: 48, marginBottom: 16 }}>📊</p>
                <p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد بيانات أداء</p>
                <p style={{ fontSize: 13, marginTop: 8 }}>
                  سيظهر أداء الفريق هنا عند بدء العمل
                </p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#080f1e" }}>
                      <th style={{ padding: "14px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                        الترتيب
                      </th>
                      <th style={{ padding: "14px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                        الموظف
                      </th>
                      <th style={{ padding: "14px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                        المطاعم المضافة
                      </th>
                      <th style={{ padding: "14px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                        الصفقات المغلقة
                      </th>
                      <th style={{ padding: "14px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                        معدل التحويل
                      </th>
                      <th style={{ padding: "14px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                        النقاط
                      </th>
                      <th style={{ padding: "14px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                        الحالة
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map((member, index) => (
                      <tr 
                        key={member.id} 
                        style={{ 
                          borderTop: "1px solid #162035",
                          background: index === 0 ? "#052e1620" : "transparent"
                        }}
                      >
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: index === 0 ? "#fbbf24" : index === 1 ? "#94a3b8" : index === 2 ? "#cd7c2f" : "#1e293b",
                            color: index < 3 ? "#000" : "#94a3b8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 900,
                            fontSize: 14,
                          }}>
                            {index + 1}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background: ROLE_META[member.role]?.color + "22",
                              border: `2px solid ${ROLE_META[member.role]?.color}44`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 18,
                            }}>
                              {ROLE_META[member.role]?.icon}
                            </div>
                            <div>
                              <div style={{ color: "#f1f5f9", fontWeight: 700, fontSize: 14 }}>
                                {member.name}
                              </div>
                              <div style={{ marginTop: 4 }}>
                                <RoleBadge role={member.role} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ color: "#34d399", fontWeight: 700, fontSize: 18 }}>
                            {member.restaurantsAdded || 0}
                          </div>
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                            إجمالي: {member.totalRestaurants || 0}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ color: "#f59e0b", fontWeight: 700, fontSize: 18 }}>
                            {member.dealsClosed || 0}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{
                            color: member.conversionRate > 30 ? "#10b981" : member.conversionRate > 15 ? "#f59e0b" : "#ef4444",
                            fontWeight: 700,
                            fontSize: 16,
                          }}>
                            {member.conversionRate || 0}%
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{
                            background: "linear-gradient(135deg, #0ea5e9, #2563eb)",
                            color: "#fff",
                            padding: "6px 12px",
                            borderRadius: 8,
                            fontWeight: 900,
                            fontSize: 16,
                            display: "inline-block",
                          }}>
                            {member.performanceScore || 0}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <OnlineIndicator 
                            isOnline={member.is_online} 
                            lastActive={member.last_active}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Performance Score Legend */}
          <div style={{
            marginTop: 24,
            background: "#080f1e",
            borderRadius: 12,
            padding: 16,
            border: "1px solid #162035"
          }}>
            <h3 style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              📊 نظام احتساب النقاط:
            </h3>
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
              gap: 12,
              color: "#94a3b8",
              fontSize: 13
            }}>
              <div>🏪 مطعم مضاف: <strong style={{ color: "#34d399" }}>+10 نقاط</strong></div>
              <div>✅ صفقة مغلقة: <strong style={{ color: "#f59e0b" }}>+50 نقطة</strong></div>
              <div>📅 يوم نشط: <strong style={{ color: "#3b82f6" }}>+5 نقاط</strong></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
